import React from "react";
import { serverTimestamp, updateDoc, type DocumentReference } from "firebase/firestore";
import type { FirebaseStorage } from "firebase/storage";
import { MAX_IMAGE_SIZE, uploadDataUrlWithProgress } from "./uploads";

export type PhotoKey = "keyboard" | "screen" | "underside" | "topside";
export type ThumbnailMap = Partial<Record<PhotoKey, string | null>>;
export type PhotoURLMap = Record<string, string>;
export type PhotoUploadResult = {
  uploaded: PhotoKey[];
  failed: PhotoKey[];
};

export const compressImage = (file: File, quality = 0.9, maxDim = 1920): Promise<string> =>
  new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let { width, height } = img;

        if (width > maxDim || height > maxDim) {
          const ratio = width > height ? maxDim / width : maxDim / height;
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        const tryCompress = (q: number) => {
          const dataUrl = canvas.toDataURL("image/jpeg", q);
          const byteLength = atob(dataUrl.split(",")[1]).length;
          if (byteLength <= MAX_IMAGE_SIZE || q <= 0.3) resolve(dataUrl);
          else tryCompress(q - 0.1);
        };
        tryCompress(quality);
      };
      img.src = (e.target as FileReader).result as string;
    };
    reader.readAsDataURL(file);
  });

type SetFormWithPhotos<T> = React.Dispatch<React.SetStateAction<T>>;

export function createHandlePhotoChange<T extends { photos: ThumbnailMap }>(
  opts: {
    setForm: SetFormWithPhotos<T>;
    setThumbnailPreviews: React.Dispatch<React.SetStateAction<ThumbnailMap>>;
    compressImageFn?: typeof compressImage;
  }
): (e: React.ChangeEvent<HTMLInputElement>, photoType: PhotoKey) => Promise<void> {
  const { setForm, setThumbnailPreviews, compressImageFn = compressImage } = opts;

  return async (
    e: React.ChangeEvent<HTMLInputElement>,
    photoType: PhotoKey
  ): Promise<void> => {
    const input = e.currentTarget;
    const file = input.files?.[0];

    // Webbläsaren skickar normalt ingen ny change-händelse om samma fil väljs
    // igen i samma input. Fånga därför File-objektet och töm native-värdet
    // direkt, innan den asynkrona komprimeringen börjar.
    input.value = "";

    if (!file) return;
    const compressedDataUrl = await compressImageFn(file);
    setForm((prev) => ({ ...prev, photos: { ...prev.photos, [photoType]: compressedDataUrl }, }));
    setThumbnailPreviews((prev) => ({
      ...prev,
      [photoType]: compressedDataUrl,
    }));
  };
}

export function createHandleRemovePhoto<T extends { photos: ThumbnailMap }>(
  opts: {
    setForm: SetFormWithPhotos<T>;
    setThumbnailPreviews: React.Dispatch<React.SetStateAction<ThumbnailMap>>;
  }
): (photoType: PhotoKey) => void {
  const { setForm, setThumbnailPreviews } = opts;

  return (photoType: PhotoKey): void => {
    setForm((prev) => ({ ...prev, photos: { ...prev.photos, [photoType]: null }, }));
    setThumbnailPreviews((prev) => ({ ...prev, [photoType]: null }));
  };
}

export const computeOverallProgress = (uploadProgress?: Record<string, number>): number => {
  const progressValues = Object.values(uploadProgress || {});
  return progressValues.length
    ? Math.round(progressValues.reduce((a, b) => a + b, 0) / progressValues.length)
    : 0;
};

export async function uploadPhotosForItem(
  params: {
    photos: ThumbnailMap;
    storage: FirebaseStorage;
    itemRef: DocumentReference;
    setUploadProgress: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  }
): Promise<PhotoUploadResult> {
  const { photos, storage, itemRef, setUploadProgress } = params;

  const photoURLs: PhotoURLMap = {};
  const attempted: PhotoKey[] = [];
  const failed: PhotoKey[] = [];
  setUploadProgress({}); // nollställ progress-kartan

  for (const [key, dataUrl] of Object.entries(photos)) {
    if (!dataUrl) continue;
    const photoKey = key as PhotoKey;
    attempted.push(photoKey);

    // initiera 0% för denna nyckel
    setUploadProgress((prev) => ({ ...prev, [key]: 0 }));

    try {
      const url = await uploadDataUrlWithProgress(
        dataUrl,
        storage,
        `photos/${itemRef.id}/${key}.jpg`,
        (pct: number) => setUploadProgress((prev) => ({ ...prev, [key]: pct }))
      );
      photoURLs[key] = url;
    } catch (e: any) {
      console.warn(`Uppladdning misslyckades för ${key}:`, e?.message || e);
      failed.push(photoKey);
      // hoppa över just den bilden och fortsätt med resten
    }
  }

  if (Object.keys(photoURLs).length > 0) {
    const updates: any = { updatedAt: serverTimestamp() };
    for (const [type, url] of Object.entries(photoURLs)) {
      updates[`photos.${type}`] = url;   // patcha slot för slot
    }
    try {
      await updateDoc(itemRef, updates);
    } catch (e: any) {
      // Filerna kan ha nått Storage, men utan Firestore-URL:erna är de inte
      // tillgängliga för användaren. Rapportera därför samtliga valda slots
      // som misslyckade och låt den redan skapade returen ligga kvar.
      console.warn("Kunde inte koppla uppladdade bilder till returen:", e?.message || e);
      return { uploaded: [], failed: attempted };
    }
  }

  const uploaded = attempted.filter((key) => !failed.includes(key));
  return { uploaded, failed };
}
